import { useNavigate } from '@tanstack/react-router'
import { Button } from '@shared/design-system/ui'
import { useMemberLibraryQuery } from '../api/member-access.queries'
import { catalogUnlockStorageKey, useCatalogUnlocks } from '../lib/catalog-unlock'
import { useMemberSession, useMemberSessionHydrated } from '../model/member-session-store'
import { MemberAccessLayout } from './MemberAccessLayout'
import { MemberCatalog } from './MemberCatalog'
import { banner, gate, pageIntro, subtitle, title, unlockLive } from './member-access.css'

export function MemberLibraryPage({ producerId }: { producerId: string }) {
  const navigate = useNavigate()
  const hydrated = useMemberSessionHydrated()
  const token = useMemberSession((state) => state.token)
  const member = useMemberSession((state) => state.member)
  const storedName = useMemberSession((state) => state.producerName)
  const library = useMemberLibraryQuery(Boolean(token))
  const items = library.data?.items ?? []
  const { unlockingIds, dismissUnlock } = useCatalogUnlocks(
    member ? catalogUnlockStorageKey(producerId, member.email) : null,
    library.data ? items : null,
  )

  if (!hydrated) return null
  if (!token) {
    void navigate({ to: '/acesso/$producerId', params: { producerId } })
    return null
  }

  const producerName = library.data?.producerName ?? storedName ?? 'Area de membros'
  if (library.data?.gate === 'EXPIRED') {
    return (
      <MemberAccessLayout producerName={producerName}>
        <section className={gate} data-testid="member-expired">
          <h1 className={title}>Acesso expirado</h1>
          <p className={subtitle}>
            Seu acesso acabou. Fale com o suporte
            {library.data.items[0]?.supportEmail ? ` em ${library.data.items[0].supportEmail}` : ''}.
          </p>
        </section>
      </MemberAccessLayout>
    )
  }
  if (library.data?.gate === 'REVOKED') {
    return (
      <MemberAccessLayout producerName={producerName}>
        <section className={gate} data-testid="member-revoked">
          <h1 className={title}>Acesso indisponivel</h1>
          <p className={subtitle}>Este conteudo nao esta mais disponivel para a sua conta.</p>
        </section>
      </MemberAccessLayout>
    )
  }

  const pastDue = library.data?.items.find((item) => item.accessStatus === 'PAST_DUE')
  const unlockedNames = items
    .filter((item) => unlockingIds.includes(item.productId))
    .map((item) => item.name)
  return (
    <MemberAccessLayout producerName={producerName}>
      {pastDue?.updatePaymentUrl ? (
        <div className={banner} data-testid="member-past-due">
          <span>Pagamento em atraso. Atualize para nao perder o acesso.</span>
          <Button size="sm" onClick={() => window.open(pastDue.updatePaymentUrl!, '_blank')}>
            Atualizar pagamento
          </Button>
        </div>
      ) : null}
      <header className={pageIntro}>
        <h1 className={title}>Meus conteudos</h1>
        <p className={subtitle}>
          {items.length === 0
            ? 'Quando um conteúdo for liberado, ele aparece aqui.'
            : 'Escolha um conteúdo para continuar. Os bloqueados ainda não fazem parte da sua compra.'}
        </p>
      </header>
      {unlockedNames.length > 0 ? (
        <p className={unlockLive} aria-live="polite">
          {unlockedNames.length === 1
            ? `${unlockedNames[0]} foi desbloqueado.`
            : `${unlockedNames.length} cursos foram desbloqueados.`}
        </p>
      ) : null}
      <MemberCatalog
        producerId={producerId}
        items={items}
        unlockingIds={unlockingIds}
        onUnlockSettled={dismissUnlock}
      />
    </MemberAccessLayout>
  )
}
